/**
 * LeadsPage — Lead-Verwaltung pro Firma: Formular-Link, Pixel-Einstellung,
 * Leads-Liste mit Status-Pflege ("gewonnen" mit Wert → purchase-Conversion).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, Copy, RefreshCw, Loader2, ExternalLink, CheckCircle2, Code } from "lucide-react";
import PageHelp from "@/components/PageHelp";
import { buildTrackingSnippet, buildEmbedSnippet } from "@/lib/snippets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = { new: "Neu", contacted: "Kontaktiert", qualified: "Qualifiziert", won: "Gewonnen", lost: "Verloren" };
const STATUS_BADGE = {
  new: "bg-blue-100 text-blue-700 border-blue-200",
  contacted: "bg-amber-100 text-amber-700 border-amber-200",
  qualified: "bg-purple-100 text-purple-700 border-purple-200",
  won: "bg-emerald-100 text-emerald-700 border-emerald-200",
  lost: "bg-gray-100 text-gray-500 border-gray-200",
};
const leadSource = (l) => {
  const ch = l.channel || "";
  if (l.source === "zefix") return { key: "firmenradar", label: ch === "akquise_auto" ? "📡 Firmenradar (Auto)" : "📡 Firmenradar", cls: "bg-violet-100 text-violet-700 border-violet-200" };
  if (ch === "website_form") {
    if (l.source === "google") return { key: "google", label: "🔍 Google Ads → Website-Formular", cls: "bg-blue-100 text-blue-700 border-blue-200" };
    if (l.source === "meta") return { key: "meta", label: "📘 Meta → Website-Formular", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" };
    if (l.source === "campaign" || l.utm_campaign) return { key: "campaign", label: `🔗 ${l.utm_campaign || "Kampagnen-Link"} (${l.utm_source || "Link"})`, cls: "bg-cyan-100 text-cyan-700 border-cyan-200" };
    return { key: "website", label: "🌐 Website-Formular", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  }
  if (l.source === "google") return { key: "google", label: "🔍 Google Ads", cls: "bg-blue-100 text-blue-700 border-blue-200" };
  if (l.source === "meta") return { key: "meta", label: "📘 Meta/Facebook", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" };
  if (l.source === "campaign" || l.utm_campaign) return { key: "campaign", label: `🔗 ${l.utm_campaign || "Kampagnen-Link"}`, cls: "bg-cyan-100 text-cyan-700 border-cyan-200" };
  if (l.source === "direct") return { key: "leadform", label: "📝 Lead-Formular (direkt)", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return { key: l.source || "unbekannt", label: l.source || "Unbekannt", cls: "bg-slate-100 text-slate-600 border-slate-200" };
};

const SOURCE_FILTERS = [
  { value: "all", label: "Alle Quellen" },
  { value: "firmenradar", label: "📡 Firmenradar" },
  { value: "website", label: "🌐 Website-Formular" },
  { value: "google", label: "🔍 Google Ads" },
  { value: "meta", label: "📘 Meta/Facebook" },
  { value: "campaign", label: "🔗 Kampagnen-Link" },
  { value: "leadform", label: "📝 Lead-Formular (direkt)" },
];

function WonDialog({ lead, open, onOpenChange, onConfirm }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="won-dialog">
        <DialogHeader>
          <DialogTitle>Lead gewonnen 🎉</DialogTitle>
          <DialogDescription>
            Auftragswert eintragen — bei Marketing-Consent wird er als Umsatz-Conversion (paid_revenue) an Google gemeldet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="won-value-input" type="number" min="0" step="0.05" placeholder="Auftragswert in CHF (optional)"
            value={value} onChange={(e) => setValue(e.target.value)} />
          <Button className="w-full" disabled={busy} data-testid="won-confirm-btn"
            onClick={async () => { setBusy(true); await onConfirm(parseFloat(value) || 0); setBusy(false); }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Als gewonnen markieren
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadsPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [wonLead, setWonLead] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => { setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id); })
      .catch(() => {});
  }, []);

  const load = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads`, { params: { company_id: cid }, withCredentials: true }),
        axios.get(`${API}/leads/settings/${cid}`, { withCredentials: true }),
      ]);
      setData(l.data);
      setSettings(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(companyId); }, [companyId, load]);

  const formUrl = companyId ? `${window.location.origin}/anfrage/${companyId}` : "";
  const trackingSnippet = companyId ? buildTrackingSnippet(companyId) : "";
  const embedCode = companyId ? buildEmbedSnippet(companyId) : "";

  const setStatus = async (lead, status, value = 0) => {
    try {
      const r = await axios.put(`${API}/leads/${lead.id}/status`, { status, value }, { withCredentials: true });
      toast.success(r.data.purchase_conversion_queued ? "Gewonnen! Umsatz-Conversion an Google gemeldet 🎉" : "Status aktualisiert");
      setWonLead(null);
      load(companyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update fehlgeschlagen");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="leads-page">
      <PageHelp
        title="Leads"
        text="Jede Firma hat ein eigenes öffentliches Anfrage-Formular. Eingehende Leads werden hier verwaltet — bei Marketing-Consent werden sie automatisch als Conversion an Google (Data Manager) und Meta (CAPI, Pixel-ID nötig) gemeldet. Markierst du einen Lead als 'Gewonnen' mit Auftragswert, geht der Umsatz als paid_revenue an Google."
      />
      <div className="flex flex-wrap items-center gap-3">
        <UserPlus className="w-6 h-6 text-[#2563EB]" />
        <h1 className="text-2xl font-bold">Leads</h1>
        <div className="flex-1" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="leads-company-select"><SelectValue placeholder="Firma" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={() => load(companyId)} data-testid="leads-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {settings && (
        <Card>
          <CardContent className="pt-4 pb-3 space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium">Öffentliches Formular:</span>
              <code className="bg-[var(--studio-bg)] border border-[var(--studio-border)] rounded px-2 py-0.5 break-all" data-testid="lead-form-url">{formUrl}</code>
              <Button size="sm" variant="ghost" className="h-6 px-2" data-testid="copy-form-url-btn"
                onClick={() => { navigator.clipboard.writeText(formUrl).then(() => toast.success("Link kopiert")).catch(() => toast.error("Kopieren nicht möglich — bitte manuell markieren")); }}>
                <Copy className="w-3 h-3" />
              </Button>
              <a href={formUrl} target="_blank" rel="noreferrer" className="text-[#2563EB] inline-flex items-center gap-1 hover:underline" data-testid="open-form-url-link">
                Öffnen <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium">Conversion-Ziele:</span>
              <Badge variant="outline" className={`text-[10px] ${settings.google_configured ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "text-amber-600 border-amber-300"}`} data-testid="google-conv-badge">
                Google {settings.google_configured ? "✓ konfiguriert" : "— nicht konfiguriert"}
              </Badge>
              <Badge variant="outline" className={`text-[10px] ${settings.meta_pixel_id ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "text-amber-600 border-amber-300"}`} data-testid="meta-conv-badge">
                Meta CAPI {settings.meta_pixel_id ? "✓ Pixel gesetzt" : "— Pixel-ID fehlt"}
              </Badge>
              <a href="/setup" className="text-[11px] text-[#2563EB] hover:underline" data-testid="pixel-setup-link">
                Pixel-ID wird im Firmen-Setup gepflegt →
              </a>
            </div>
            <div className="space-y-1.5">
              <button
                data-testid="toggle-embed-btn"
                onClick={() => setShowEmbed((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-medium hover:underline"
              >
                <Code className="w-3.5 h-3.5" /> Einbett-Code für die Firmen-Website {showEmbed ? "ausblenden" : "anzeigen"}
              </button>
              {showEmbed && (
                <div className="space-y-1.5" data-testid="embed-section">
                  <p className="text-[11px] text-[var(--studio-text-muted)]">
                    Diesen Code in die Website (z.B. reinmove.ch) einfügen — das Formular erscheint als Widget und übernimmt automatisch gclid/fbclid aus Werbeklicks für die Conversion-Zuordnung.
                  </p>
                  <textarea
                    data-testid="embed-code-textarea"
                    readOnly
                    value={embedCode}
                    className="w-full h-40 text-[10px] font-mono bg-[var(--studio-bg)] border border-[var(--studio-border)] rounded-lg p-2"
                  />
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="copy-embed-btn"
                    onClick={() => { navigator.clipboard.writeText(embedCode).then(() => toast.success("Einbett-Code kopiert")).catch(() => toast.error("Kopieren nicht möglich — bitte manuell markieren")); }}>
                    <Copy className="w-3 h-3" /> Code kopieren
                  </Button>
                  <p className="text-[11px] text-[var(--studio-text-muted)] pt-1">
                    <strong>Zusätzlich empfohlen — Tracking-Snippet auf ALLEN Seiten:</strong> Speichert die Werbe-Klick-ID (gclid/fbclid) beim ersten Besuch, damit sie auch erhalten bleibt, wenn der Besucher erst auf einer Unterseite das Formular ausfüllt.
                  </p>
                  <textarea
                    data-testid="tracking-snippet-textarea"
                    readOnly
                    value={trackingSnippet}
                    className="w-full h-24 text-[10px] font-mono bg-[var(--studio-bg)] border border-[var(--studio-border)] rounded-lg p-2"
                  />
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="copy-tracking-btn"
                    onClick={() => { navigator.clipboard.writeText(trackingSnippet).then(() => toast.success("Tracking-Snippet kopiert")).catch(() => toast.error("Kopieren nicht möglich — bitte manuell markieren")); }}>
                    <Copy className="w-3 h-3" /> Snippet kopieren
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <div className="flex items-center gap-2 text-sm text-[var(--studio-text-muted)]"><Loader2 className="w-4 h-4 animate-spin" /> Lade Leads…</div>}

      {data && !loading && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex flex-wrap items-center gap-2">
              Eingegangene Anfragen
              {Object.entries(data.counts).map(([k, v]) => v > 0 && (
                <Badge key={k} variant="outline" className={`text-[10px] ${STATUS_BADGE[k]}`}>{STATUS_LABELS[k]}: {v}</Badge>
              ))}
              <div className="ml-auto">
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="h-7 w-52 text-xs" data-testid="lead-source-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="leads-list">
            {data.items.length === 0 && (
              <p className="text-sm text-[var(--studio-text-muted)] py-4 text-center" data-testid="leads-empty">
                Noch keine Leads. Teile den Formular-Link oder verlinke ihn aus deinen Anzeigen (gclid/fbclid werden automatisch erfasst).
              </p>
            )}
            {data.items.filter((l) => sourceFilter === "all" || leadSource(l).key === sourceFilter).map((l) => (
              <div key={l.id} className="border border-[var(--studio-border)] rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid={`lead-row-${l.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {l.name}
                    <Badge variant="outline" className={`text-[9px] ${STATUS_BADGE[l.status]}`}>{STATUS_LABELS[l.status]}</Badge>
                    <Badge variant="outline" className={`text-[9px] ${leadSource(l).cls}`} data-testid={`lead-source-${l.id}`}>{leadSource(l).label}</Badge>
                    {l.purchase_value > 0 && <span className="text-[10px] text-emerald-600 font-medium">CHF {l.purchase_value}</span>}
                    {l.zefix?.found && (
                      <a href={l.zefix.excerpt_url || "#"} target="_blank" rel="noreferrer" title={`Handelsregister: ${l.zefix.name} · ${l.zefix.seat} · ${l.zefix.uid}`}>
                        <Badge className={`text-[9px] ${l.zefix.active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`} data-testid={`lead-zefix-badge-${l.id}`}>
                          HR ✓ {l.zefix.legal_form || ""} {l.zefix.seat}
                        </Badge>
                      </a>
                    )}
                    {l.zefix && l.zefix.found === false && (
                      <Badge variant="outline" className="text-[9px] text-slate-400" title="Firma nicht im Handelsregister gefunden">HR: kein Eintrag</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[var(--studio-text-muted)]">
                    {[l.email, l.phone].filter(Boolean).join(" · ")} · {new Date(l.created_at).toLocaleString("de-CH")}
                  </div>
                  {l.message && <div className="text-xs mt-1 text-[var(--studio-text)] line-clamp-2">"{l.message}"</div>}
                  <div className="flex gap-2 mt-1 text-[9px] text-[var(--studio-text-muted)]">
                    <span title="Google Conversion">G: {l.google_status}</span>
                    <span title="Meta CAPI">M: {l.meta_status}</span>
                    {l.consent ? <span className="text-emerald-600 inline-flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> Consent</span> : <span>kein Consent</span>}
                  </div>
                </div>
                <Select value={l.status} onValueChange={(v) => (v === "won" ? setWonLead(l) : setStatus(l, v))}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid={`lead-status-select-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <WonDialog
        lead={wonLead}
        open={!!wonLead}
        onOpenChange={(v) => { if (!v) setWonLead(null); }}
        onConfirm={(value) => setStatus(wonLead, "won", value)}
      />
    </div>
  );
}
